import { MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { TextField } from "../../components/fields/Fields";
import { SearchUserList, UserListSearchDefault, UserData } from "../../model/Users";
import { ConstMessage } from "../../config/const";
import Button from "../../components/button/Button";
import Badge from "../../components/badges/Badges";
import { CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import { GroupSelectModal } from "../userList/GroupSelectModal";
import { SetStateAction } from "react";
import { Company } from "../../model/Companies";

type Props = {
  setSelectGroup: (value: SetStateAction<{id:string, title:string}[]>) => void;
  selectGroup: {id:string, title:string}[];
  setUserListResult: (value: SetStateAction<SearchUserList>) => void;
  userListResult: SearchUserList;
  userList: UserData[] | undefined;
  company: Company | undefined;
  setSearchUserList: Function;
  setSearchUserListFlg: Function;
  setGroupsValue: (value: SetStateAction<CheckBoxItem[]>) => void;
  groupsValue: CheckBoxItem[];
  defaultGroups: CheckBoxItem[];
  onSearch: (searchCondition: any) => void;
  searchCondition: any;
};
  
export const UserListSearch = ({
  setSelectGroup,
  selectGroup,
  setUserListResult,
  userListResult,
  userList,
  company,
  setSearchUserList,
  setSearchUserListFlg,
  setGroupsValue,
  groupsValue,
  defaultGroups,
  onSearch,
  searchCondition,
}:Props) => {

  // アイコン設定
  const searchIcon = {icon: MagnifyingGlassIcon};
  const upIcon = {icon: ChevronUpIcon};
  const downIcon = {icon: ChevronDownIcon};

  const [searchFlg, setSearchFlg] = useState(true);
  const [userIdError, setUserIdError] = useState(false);
  const [isOpenGroupSelect, setIsOpenGroupSelect] = useState(false);
  const [isVoid, setIsVoid] = useState(false);
  const [userDataNewList, setMessageNewList] = useState<UserData[]>(userList ?? []);
  const [oldGroupsValue, setOldGroupsValue] = useState<CheckBoxItem[]>([]);
  const [groupSelectError, setGroupSelectError] = useState("");
  
  const errorMessage = ConstMessage.ERR_USERID_LENGTH;

  // 検索画面の表示・非表示の切り替え
  const handleClick = (e:any) => {
    e.preventDefault();
    setSearchFlg(searchFlg? false : true);
  };

  const toggle = () => {
    // 30件制限チェック
    const selectedCount = groupsValue.filter((g) => g.value === true).length;
    if (selectedCount > 30) {
      setGroupSelectError(ConstMessage.ERR_GROUP_SELECT_31_OR_MORE);
      return;
    }
    setGroupSelectError("");
    setOldGroupsValue(groupsValue);
    setIsOpenGroupSelect(!isOpenGroupSelect);
    setIsVoid(!isVoid);
  };

  const onGroupSelectCancel = () => {
    setIsOpenGroupSelect(!isOpenGroupSelect);
    setGroupsValue(oldGroupsValue);
    setIsVoid(!isVoid);
  };

  // グループセレクト画面の表示用
  useEffect(() => {
    const f = async () => {
      setIsVoid(!isVoid);
      setOldGroupsValue(groupsValue);
      if(groupsValue.length > 0){
        let topic: any = [];
        await Promise.all(groupsValue.map((item: any) => {
          if(item.value){
            topic.push({id:item.id, title: item.title});
          }
        }));
        setSelectGroup(topic);
        setUserListResult((prev) => ({ ...prev, topic }));
      } else {
        setSelectGroup([]);
        setUserListResult((prev) => ({ ...prev, topic: [] }));
      }
    };
    f();
  }, [isOpenGroupSelect]);

  // 検索を実行
  const onClickSearch = async () => {
    if (userListResult.userId && userListResult.userId.trim() !== "") {
      const regexp = /^[0-9]{11}$/;
      if (!regexp.test(userListResult.userId)) {
        setUserIdError(true);
        return;
      } else {
        setUserIdError(false);
      }
    } else {
      setUserIdError(false);
    }
    const searchCondition = {
      userId: userListResult.userId,
      name: userListResult.name || "",
      groups: Array.isArray(userListResult.topic)
        ? userListResult.topic.map((g: any) => g.id)
        : [],
    };
    onSearch(searchCondition);
  };

  return(
    <div className="flex flex-col">
      <div className="inline-block min-w-full p-1 align-middle">
        <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y">
            <div className="items-center p-2">
              <div className="flex justify-between cursor-pointer" onClick={(e) => handleClick(e)}>
                <h2 className=" flex font-semibold text-gray-900 items-center">
                  <searchIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                  />
                  検索
                </h2>
                {searchFlg?(
                  <>
                    <upIcon.icon
                      className={"flex-shrink-0 h-5 w-5"}
                      aria-hidden="true"
                    />
                  </>
                ):(
                  <>
                    <downIcon.icon
                      className={"flex-shrink-0 h-5 w-5"}
                      aria-hidden="true"
                    />
                  </>
                )}
              </div>
              <div className="pr-10 flex flex-wrap max-w-[100%]">
                {searchFlg?(
                  <>
                    <div className="flex">
                      <div className="h-10 ml-4 mb-2 flex justify-start flex-1 flex-wrap">
                        <h3 className="text-sm text-gray-900 mr-7 mt-2">利用者番号</h3>&nbsp;
                        <div className="w-1/10 items-center">
                          <TextField
                            id="userId"
                            className={'h-full w-48'}
                            inputClassName={`h-8`}
                            value={userListResult.userId}
                            error={userIdError}
                            helperText={errorMessage}
                            onChange={(e:any) => setUserListResult({...userListResult, userId:e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="h-10 ml-24 flex">
                        <h3 className="text-sm text-gray-900 mr-7 mt-2">名前</h3>&nbsp;
                        <div className="w-1/10 items-center">
                          <TextField
                            id="userName"
                            className={'h-full w-48'}
                            inputClassName={`h-8`}
                            value={userListResult.name}
                            onChange={(e:any) => setUserListResult({...userListResult, name:e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex w-full">
                      <div className="flex w-1/2 mt-4">
                        <div className="pl-4 mt-4 w-auto">
                          <div className="mt-2 flex items-center justify-start flex-1 flex-wrap">
                            <h3 className="text-sm text-gray-900 mr-2 whitespace-nowrap">所属グループ </h3>
                          </div>
                        </div>
                        <div className="pl-2 mt-2 w-2/3 min-w-[330px]">
                          <div className={`text-left items-start`}>
                            <div className="h-12 w-full border-2 border-solid overflow-y-scroll">
                              {selectGroup.map(
                                (item: any) => (
                                  <Badge
                                    color="blue"
                                  >
                                    {item.title}
                                  </Badge>
                                ))
                              }
                            </div>
                          </div>
                          <h3 className="text-xs text-gray-500 ml-2 whitespace-nowrap">複数選択した場合いずれかに所属する利用者すべてが検索されます</h3>
                        </div>
                        <div className="ml-1 mt-2 w-auto">
                          <div className="flex items-center justify-start flex-1 flex-wrap">
                            <div className="flex w-24">
                              <Button
                                className={"h-12 w-24 mr-2 pl-4 pr-4 text-sm items-center rounded-full"}
                                onClick={toggle}
                                children={"グループ選択"}
                              />
                            </div>
                          </div>
                        </div>
                        <GroupSelectModal
                          open={isOpenGroupSelect}
                          toggle={toggle}
                          closeOnClickOutside
                          companyGroupsValue={groupsValue}
                          setState={setGroupsValue}
                          children={undefined}
                          dispButtonFlg={true}
                          onSelectCancel={onGroupSelectCancel}
                        />
                      </div>
                      <div className={`flex w-1/2 justify-end`}>
                        <Button
                          className={"mt-8 ml-2 h-10 w-32 text-sm items-center"}
                          onClick={onClickSearch}
                          children={"検索"}
                        />
                      </div>
                    </div>
                  </>
                ):(
                  <>
                  </>
                )}
              </div>
            </div>
          </table>
        </div>
      </div>
    </div>
  );
};